import type {Header845, ItemLoop845, NameLoop845, Parsed845} from './parser845';
import {entriesOf} from './sections';
import type {Section, SectionGroup, SectionEntry} from './sections';

type CommonSectionKey845 =
  | 'order'
  | 'dates'
  | 'parties'
  | 'terms'
  | 'items'
  | 'notes'
  | 'totals';

const SECTION_ORDER_845: CommonSectionKey845[] = [
  'order',
  'dates',
  'parties',
  'terms',
  'items',
  'notes',
  'totals',
];

type HeaderKey845 = keyof Header845;

const HEADER_SECTION_845: Record<HeaderKey845, CommonSectionKey845> = {
  bpa: 'order',
  con: 'order',
  refs: 'order',
  per: 'order',
  dtm: 'dates',
  msg: 'notes',
  n9: 'notes',
  other: 'order',
};

const SECTION_META_845: Record<
  CommonSectionKey845,
  {title: string; description: string}
> = {
  order: {
    title: 'Price authorization details',
    description:
      'Authorization type and number, contract reference and reference numbers.',
  },
  dates: {
    title: 'Dates',
    description: 'Dates on the price authorization.',
  },
  parties: {
    title: 'Parties & addresses',
    description: 'Who is involved and where.',
  },
  terms: {
    title: 'Terms',
    description: 'Not used by the 845 — always empty.',
  },
  items: {
    title: 'Price authorization lines',
    description: 'Each authorized price, its contract pricing and quantity.',
  },
  notes: {
    title: 'Notes',
    description: 'Free-form messages on the price authorization.',
  },
  totals: {
    title: 'Totals',
    description: 'Line-item count and authorization totals.',
  },
};

const NAME_LOOP_KEYS_845: ReadonlyArray<keyof NameLoop845> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'n5',
  'n6',
  'n9',
  'refs',
  'per',
  'other',
];

const ITEM_KEYS_845: ReadonlyArray<keyof ItemLoop845> = [
  'pad',
  'ctp',
  'qty',
  'other',
];

export function buildSections845(parsed: Parsed845, txPath: string): Section[] {
  const flat: Record<CommonSectionKey845, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    terms: [],
    items: [],
    notes: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_845) as HeaderKey845[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_845[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(
    ...entriesOf(parsed.summary, ['ctt', 'amt'], `${txPath}.summary`)
  );

  const partyGroups: SectionGroup[] = parsed.nameLoops.map((loop, i) => {
    const path = `${txPath}.nameLoops[${i}]`;
    return {
      key: path,
      entries: entriesOf(loop, NAME_LOOP_KEYS_845, path),
    };
  });

  const itemGroups: SectionGroup[] = parsed.items.map((item, i) => {
    const path = `${txPath}.items[${i}]`;
    return {
      key: path,
      entries: entriesOf(item, ITEM_KEYS_845, path),
    };
  });

  return SECTION_ORDER_845.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'items';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : itemGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_845[key], repeating, groups}];
  });
}
